/**
 * P3-07 · Custom options & personalisation contract.
 *
 * `template:brandable` — whether a product carries custom fields, and their
 * definitions, are DATA (`Product.customOptions`), never code. This module is
 * the single source of truth shared by three surfaces:
 *  - the PDP loader (parses the stored JSON into typed, validated definitions),
 *  - the `CustomOptions` UI (live validation + price delta for display),
 *  - the add-to-cart mutation (P3-08) and cart persistence (P4), which call
 *    {@link validatePersonalisation} to RECOMPUTE the price delta server-side.
 *
 * The client delta is never trusted: it is display-only. `validatePersonalisation`
 * recomputes the authoritative delta from the definitions on every submit.
 */
import { z } from "zod";

/** Field kinds a product may define. */
export const CUSTOM_OPTION_TYPES = ["TEXT", "TEXTAREA", "SELECT", "CHECKBOX"] as const;
export type CustomOptionType = (typeof CUSTOM_OPTION_TYPES)[number];

/** Soft cap on definitions per product (P3-07 open question — admin-enforced). */
export const MAX_CUSTOM_OPTIONS = 10;
/** Hard ceiling on a text/monogram field, even when a def omits `maxLength`. */
export const CUSTOM_TEXT_HARD_MAX = 500;

/** One dropdown choice, carrying its own price delta (minor units). */
export const customOptionChoiceSchema = z.object({
  label: z.string().trim().min(1).max(120),
  priceDeltaMinor: z.number().int().min(0).default(0),
});
export type CustomOptionChoice = z.infer<typeof customOptionChoiceSchema>;

/**
 * A single custom-field definition. Refined per type: SELECT must carry a
 * non-empty `options` list; TEXT/TEXTAREA may cap length and charge a flat
 * delta when filled; CHECKBOX charges its delta when ticked.
 */
export const customOptionDefSchema = z
  .object({
    id: z.string().trim().min(1).max(64),
    label: z.string().trim().min(1).max(120),
    type: z.enum(CUSTOM_OPTION_TYPES),
    required: z.boolean().default(false),
    /** TEXT/TEXTAREA only — clamped to the hard ceiling. */
    maxLength: z.number().int().min(1).max(CUSTOM_TEXT_HARD_MAX).optional(),
    /** SELECT only — the dropdown choices. */
    options: z.array(customOptionChoiceSchema).min(1).max(50).optional(),
    /** Flat delta for TEXT/TEXTAREA (when non-empty) and CHECKBOX (when ticked). */
    priceDeltaMinor: z.number().int().min(0).optional(),
  })
  .refine((d) => d.type !== "SELECT" || (d.options?.length ?? 0) > 0, {
    message: "SELECT options must define at least one choice",
  });
export type CustomOptionDef = z.infer<typeof customOptionDefSchema>;

/**
 * Untrusted `Product.customOptions` JSON → validated definitions. Never throws:
 * a non-array blob yields `[]`, and each element is validated INDIVIDUALLY so a
 * single malformed field degrades to being dropped rather than nuking every
 * option on the product. Duplicate ids collapse to the first occurrence (so
 * downstream keying stays 1:1) and the result is capped at the soft max.
 */
export function parseCustomOptions(raw: unknown): CustomOptionDef[] {
  if (!Array.isArray(raw)) return [];
  const out: CustomOptionDef[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const parsed = customOptionDefSchema.safeParse(item);
    if (!parsed.success || seen.has(parsed.data.id)) continue;
    seen.add(parsed.data.id);
    out.push(parsed.data);
    if (out.length >= MAX_CUSTOM_OPTIONS) break;
  }
  return out;
}

/** A submitted value for one field: text for TEXT/TEXTAREA/SELECT, bool for CHECKBOX. */
export const personalisationValueSchema = z.union([z.string(), z.boolean()]);
/** The raw personalisation payload keyed by definition id (add-to-cart input). */
export const personalisationValuesSchema = z.record(z.string(), personalisationValueSchema);
export type PersonalisationValues = z.infer<typeof personalisationValuesSchema>;

export type PersonalisationResult =
  | {
      ok: true;
      /** Normalised values (trimmed text, coerced booleans), definition order. */
      values: PersonalisationValues;
      /** Server-authoritative sum of selected deltas, in minor units. */
      priceDeltaMinor: number;
    }
  | {
      ok: false;
      errors: Record<string, string>;
      /**
       * Best-effort delta from the currently-valid selections — so the buy box
       * can show a live price while required fields are still incomplete. Never
       * used as the charge: only the `ok: true` delta is authoritative.
       */
      priceDeltaMinor: number;
    };

/** The trimmed string for a field, or "" when absent/non-string. */
function asText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Validate a personalisation payload against a product's definitions and
 * recompute the authoritative price delta. Pure and isomorphic — the client
 * uses it for live UI, the server as the source of truth on add. Unknown keys
 * (a value for an id the product doesn't define) are a hard error, not silently
 * dropped, so a tampered payload fails loudly rather than mispricing.
 */
export function validatePersonalisation(
  defs: readonly CustomOptionDef[],
  rawValues: unknown,
): PersonalisationResult {
  const values: PersonalisationValues =
    rawValues && typeof rawValues === "object" && !Array.isArray(rawValues)
      ? (rawValues as PersonalisationValues)
      : {};
  const errors: Record<string, string> = {};
  const clean: PersonalisationValues = {};
  let priceDeltaMinor = 0;

  const known = new Set(defs.map((d) => d.id));
  for (const key of Object.keys(values)) {
    if (!known.has(key)) errors[key] = "Unknown option";
  }

  for (const def of defs) {
    const raw = values[def.id];
    switch (def.type) {
      case "TEXT":
      case "TEXTAREA": {
        const text = asText(raw);
        const max = def.maxLength ?? CUSTOM_TEXT_HARD_MAX;
        if (def.required && text.length === 0) {
          errors[def.id] = `${def.label} is required`;
        } else if (text.length > max) {
          errors[def.id] = `${def.label} must be ${max} characters or fewer`;
        }
        if (text.length > 0) {
          clean[def.id] = text;
          if (def.priceDeltaMinor) priceDeltaMinor += def.priceDeltaMinor;
        }
        break;
      }
      case "SELECT": {
        const choice = asText(raw);
        const match = def.options?.find((o) => o.label === choice);
        if (choice.length === 0) {
          if (def.required) errors[def.id] = `Select ${def.label.toLowerCase()}`;
        } else if (!match) {
          errors[def.id] = `Invalid ${def.label.toLowerCase()} choice`;
        } else {
          clean[def.id] = match.label;
          priceDeltaMinor += match.priceDeltaMinor;
        }
        break;
      }
      case "CHECKBOX": {
        const checked = raw === true;
        if (def.required && !checked) {
          errors[def.id] = `${def.label} is required`;
        }
        if (checked) {
          clean[def.id] = true;
          if (def.priceDeltaMinor) priceDeltaMinor += def.priceDeltaMinor;
        }
        break;
      }
    }
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors, priceDeltaMinor };
  }
  return { ok: true, values: clean, priceDeltaMinor };
}
